import React, { ReactNode, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { isPublicEntryPath } from '../utils/publicRoutes';
import { ADMIN_ROLES } from '../constants/roles';

interface AuthGuardProps {
  children?: ReactNode;
  fallback?: ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback = <div>Please log in to access this page.</div>
}) => {
  const { isAuthenticated, login, isLoading, user } = useAuth();
  const navigate = useNavigate();

  // Single login redirect — login() is idempotent in AuthProvider.
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      // Store the current location so we can redirect back after authentication
      const { pathname } = window.location;
      const currentPath = pathname + window.location.search;
      if (!isPublicEntryPath(pathname)) {
        sessionStorage.setItem('intendedDestination', currentPath);
      }
      void login();
    }
  }, [isAuthenticated, isLoading, login]);

  // Handle role-based redirects for authenticated users
  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      if (!ADMIN_ROLES.some((r) => user.roles?.includes(r))) {
        navigate('/access-denied', { replace: true });
      }
    }
  }, [isAuthenticated, user, isLoading, navigate]);

  // Show loading while authentication is being determined
  if (isLoading) {
    return <div>Loading authentication...</div>;
  }

  // If not authenticated, show redirect message (login will be called by useEffect)
  if (!isAuthenticated) {
    return <div>Redirecting to login...</div>;
  }

  // User is authenticated, render children or nested routes
  return children ? <>{children}</> : <Outlet />;
};

export default AuthGuard;